/* eslint eqeqeq: "off" */
import React, { memo, useState, useEffect } from 'react';
import MUIDataTable from 'mui-datatables';
import { Chip, IconButton, Badge, Button, } from '@material-ui/core';
import ChatBubbleIcon from '@material-ui/icons/ChatBubble';
import { dealerNeedsReviewCell, isConciergeCell } from '../../utils/cellRenderers';
import { centsToDollar, emailStringArrayToArray } from '../../utils/formatters';
import { saveTableSession, getTableSession } from '../../utils/sessionHelpers';
import SettingsApplicationsTwoToneIcon from '@material-ui/icons/SettingsApplicationsTwoTone';
import { useRole } from '../../contexts/RolesContext';
import { FlagCell, FincenIdCell } from '../../components';
import InlineLoadingText from '../../components/InlineLoadingText';
import { useError } from '../../contexts/ErrorContext';
import { debounceSearchRender } from 'mui-datatables';
import { DealerService } from '../../services';

const ROWS_PER_PAGE = 15;

// TODO: Allow filtering by roles. Need custom filtering or rendering logic
// Note: Needed function to pass in functions from props

const defaultColumnOptions = {
    filter: false,
    sort: false,
}

const getColumns = (toggleShowUpdateDealer, setCurrentDealer, isDealerBrowser, visibleColumns) => [
    {
        name: 'Dealer ID',
        options: {
            ...defaultColumnOptions,
            display: false,
            viewColumns: false
        }
    },
    {
        name: 'Dealer Number',
        options: {
            ...defaultColumnOptions,
            display: visibleColumns ? visibleColumns.includes('Dealer Number') : true,
            customBodyRender: (value, tableMeta) => {
                return <a href={`/dealers/${value}`}>{value}</a>
            }
        }
    },
    {
        name: 'Name',
        options: {
            ...defaultColumnOptions,
            display: visibleColumns ? visibleColumns.includes('Name') : true,
        }
    },
    {
        name: 'Organization',
        options: {
            ...defaultColumnOptions,
            display: visibleColumns ? visibleColumns.includes('Organization') : true,
        }
    },
    {
        name: 'Affiliate',
        options: {
            ...defaultColumnOptions,
            display: visibleColumns ? visibleColumns.includes('Affiliate') : true,
        }
    },
    {
        name: "Account Flag",
        options: {
            ...defaultColumnOptions,
            filter: true,
            display: visibleColumns ? visibleColumns.includes('Account Flag') : true,
            customBodyRender: dealerNeedsReviewCell,
            customFilterListOptions: {
                render: v => v ? "Account Flagged" : "Account Not Flagged",
            },
        }
    },
    {
        name: 'Comments',
        options: {
            ...defaultColumnOptions,
            display: visibleColumns ? visibleColumns.includes('Comments') : true,
            customBodyRender: (value) => {
                return (value == 0) ? null : (
                    <Badge color="primary" badgeContent={value} max={9} overlap='rectangular'>
                        <ChatBubbleIcon />
                    </Badge>
                )
            }
        }
    },
    {
        name: "Manual Flag",
        options: {
            ...defaultColumnOptions,
            display: visibleColumns ? visibleColumns.includes('Manual Flag') : true,
            customBodyRender: (value, tableMeta) => {
                return (
                    <FlagCell
                        value={value}
                        dealerNumber={tableMeta.rowData[1]}
                        flagReason={tableMeta.rowData[18]}
                    />
                );
            },
        }
    },
    {
        name: "Loan Count",
        options: {
            ...defaultColumnOptions,
            display: visibleColumns ? visibleColumns.includes('Loan Count') : true,
        }
    },
    {
        name: "Avg Age",
        options: {
            ...defaultColumnOptions,
            display: visibleColumns ? visibleColumns.includes('Avg Age') : true,
            viewColumns: true,
        }
    },
    {
        name: "Avg Credit",
        options: {
            ...defaultColumnOptions,
            display: visibleColumns ? visibleColumns.includes('Avg Credit') : true,
            viewColumns: true,
        }
    },
    {
        name: "90 Day Credit Score",
        options: {
            ...defaultColumnOptions,
            display: visibleColumns ? visibleColumns.includes('90 Day Credit Score') : true,
            viewColumns: true,
        }
    },
    {
        name: "90 Day Delinquency Rate",
        options: {
            ...defaultColumnOptions,
            display: visibleColumns ? visibleColumns.includes('90 Day Delinquency Rate') : true,
            viewColumns: true,
            customBodyRender: (value) => {
                return `${value}%`;
            }
        }
    },
    {
        name: "Funded YTD",
        options: {
            ...defaultColumnOptions,
            display: visibleColumns ? visibleColumns.includes('Funded YTD') : true,
            viewColumns: true,
        }
    },
    {
        name: "Max Loan Amount",
        options: {
            ...defaultColumnOptions,
            display: visibleColumns ? visibleColumns.includes('Max Loan Amount') : true,
        }
    },
    {
        name: "Total Amount Loaned",
        options: {
            ...defaultColumnOptions,
            display: visibleColumns ? visibleColumns.includes('Total Amount Loaned') : true,
        }
    },
    {
        name: "Average Loan Amount",
        options: {
            ...defaultColumnOptions,
            display: visibleColumns ? visibleColumns.includes('Average Loan Amount') : true,
        }
    },
    {
        name: 'Notification Emails',
        options: {
            ...defaultColumnOptions,
            display: visibleColumns ? visibleColumns.includes('Notification Emails') : true,
            customBodyRender: (values) => {
                if (values !== null) {
                    return values.map(value => <Chip label={value} />)
                } else {
                    return null;
                }
            }
        }
    },
    {
        name: 'Manual Flag Reason',
        options: {
            ...defaultColumnOptions,
            display: false,
            viewColumns: false
        }
    },
    {
        name: 'Concierge Dealer',
        options: {
            ...defaultColumnOptions,
            filter: true,
            display: visibleColumns ? visibleColumns.includes('Concierge Dealer') : true,
            customBodyRender: isConciergeCell,
            customFilterListOptions: {
                render: v => v ? "Concierge Dealers" : "Non Concierge Dealers",
            },
        }
    },
    {
        name: 'Config',
        options: {
            ...defaultColumnOptions,
            display: visibleColumns ? visibleColumns.includes('Config') : true,
            customBodyRender: (value, tableMeta) => {
                return isDealerBrowser() ? null : (
                    <IconButton onClick={() => {
                        setCurrentDealer({
                            dealerNumber: tableMeta.rowData[1],
                            dealerName: tableMeta.rowData[2],
                            routingNumber: tableMeta.rowData[21],
                            accountNumber: tableMeta.rowData[22],
                            notificationEmails: tableMeta.rowData[17],
                            affiliate: tableMeta.rowData[4],
                            organization: tableMeta.rowData[3]
                        });
                        toggleShowUpdateDealer();
                    }}>
                        <SettingsApplicationsTwoToneIcon fontSize="large" />
                    </IconButton>
                );
            }
        }
    },
    {
        name: 'Routing Number',
        options: {
            ...defaultColumnOptions,
            display: false,
            viewColumns: false
        }
    },
    {
        name: 'Account Number',
        options: {
            ...defaultColumnOptions,
            display: false,
            viewColumns: false
        }
    },
    {
        name: 'Vision Dealer',
        options: {
            ...defaultColumnOptions,
            display: false,
            viewColumns: false
        }
    },
    {
        name: 'Delinquent Loans',
        options: {
            ...defaultColumnOptions,
            display: visibleColumns ? visibleColumns.includes('Delinquent Loans') : true,
            viewColumns: true,
        }
    },
    {
        name: 'Delinquency Rate',
        options: {
            ...defaultColumnOptions,
            display: visibleColumns ? visibleColumns.includes('Delinquency Rate') : true,
            viewColumns: true,
            customBodyRender: (value) => {
                return `${value}%`;
            }
        }
    },
    {
        name: 'Delinquent for 30+ Days Rate',
        options: {
            ...defaultColumnOptions,
            display: visibleColumns ? visibleColumns.includes('Delinquent for 30+ Days Rate') : true,
            viewColumns: true,
            customBodyRender: (value) => {
                return `${value}%`;
            }
        }
    },
    {
        name: 'Loan Types',
        options: {
            ...defaultColumnOptions,
            display: false,
            viewColumns: false,
        }
    },
    {
        name: 'Create Date',
        options: {
            ...defaultColumnOptions,
            display: visibleColumns ? visibleColumns.includes('Create Date') : true,
            viewColumns: true,
        }
    },
    {
        name: 'Test Trxns',
        options: {
            ...defaultColumnOptions,
            filter: true,
            display: visibleColumns ? visibleColumns.includes('Test Trxns') : true,
            viewColumns: true,
        }
    },
    {
        name: 'FinCEN ID',
        options: {
            ...defaultColumnOptions,
            filter: true,
            display: true,
            customBodyRender: (value, tableMeta) => {
                return (
                    <FincenIdCell
                        value={value}
                        dealerNumber={tableMeta.rowData[1]}
                        dealerName={tableMeta.rowData[2]}
                    />
                );
            },
            customFilterListOptions: {
                render: v => v ? "With FinCEN ID" : "Without FinCEN ID",
            },
        }
    }
];

const getTableOptions = (setTableConfig, count, page, sortOrder, searchText) => {
    return {
        serverSide: true,
        filterType: 'dropdown',
        selectableRows: 'none',
        print: false,
        download: false,
        rowsPerPage: ROWS_PER_PAGE,
        rowsPerPageOptions: [],
        page: page,
        sortOrder: sortOrder,
        searchText: searchText,
        confirmFilters: true,
        expandableRowsHeader: false,
        count: count,
        customFilterDialogFooter: (currentFilterList, applyNewFilters) => {
            return (
                <div style={{ marginTop: '40px' }}>
                    <Button variant="contained" onClick={() => changeFilter(setTableConfig, applyNewFilters)}>Apply Filters</Button>
                </div>
            );
        },
        onFilterChange: (column, filterList, type) => {
            if (type === 'chip') {
                // Chip is removed
                let newFilters = () => (filterList);
                changeFilter(setTableConfig, newFilters);
            }
        },
        jumpToPage: true,
        customSearchRender: debounceSearchRender(2000),
        onRowClick: rowData => {},
        onTableChange: (action, tableState) => {
            switch (action) {
                case 'changePage':
                    changePage(setTableConfig, tableState.page);
                    break;
                case 'sort':
                    changeSort(setTableConfig, tableState.sortOrder);
                    break;
                case 'search':
                    changeSearch(setTableConfig, tableState.searchText);
                    break;
                // NOTE: 'filterChange case is handled above in onFilterChange (for chips) and
                // by clicking the Apply Filters button of customFilterDialogFooter
                case 'resetFilters':
                    changeFilter(setTableConfig, []);
                    break;
                case 'viewColumnsChange':
                    let displayedVisibleColumns = tableState.columns.map(column => {
                        if (column.display === "true") {
                            return column.name;
                        } else {
                            return null;
                        }
                    }).filter(column => column !== null);

                    setTableConfig(state => ({
                        ...state,
                        visibleColumns: displayedVisibleColumns
                    }));

                    if (sessionStorage.getItem("dealerTable")) {
                        saveTableSession(displayedVisibleColumns, "dealerTable");
                    }

                    break;
                default:
                    break;
            }
        }
    };
};

const tablifyDealers = (dealers, isLoading) =>
    isLoading
    ? null
    : dealers.map(dealer => {
        return [
            dealer.id,
            dealer.dealerNumber,
            dealer.legalBusinessName ?? dealer.businessName,
            dealer.organization,
            dealer.affiliate,
            dealer.needsReview,
            dealer.commentCount,
            dealer.isManualFlagged,
            dealer.dealerMetric?.totalFundedLoans,
            dealer.avgAge,
            dealer.creditScores,
            dealer.dealerMetric?.ninetyDayAverageCreditScorePb,
            dealer.dealerMetric?.ninetyDayDelinquencyRate ?? '0',
            centsToDollar(dealer.fundedYTD),
            centsToDollar(dealer.dealerMetric?.maxLoanAmount),
            centsToDollar(dealer.dealerMetric?.totalLoanAmount),
            centsToDollar(dealer.dealerMetric?.averageLoanAmount),
            emailStringArrayToArray(dealer.notificationEmails),
            dealer.manualFlaggedReason,
            dealer.isConcierge,
            null,
            dealer.routingNumber,
            dealer.accountNumber,
            dealer.hasVisionLoan,
            dealer.dealerMetric?.delinquentLoansCount,
            dealer.dealerMetric?.totalFundedLoans ? ((dealer.dealerMetric.delinquentLoansCount / dealer.dealerMetric.totalFundedLoans) * 100).toFixed(2) : '0.00',
            dealer.dealerMetric?.totalFundedLoans && dealer.dealerMetric.delinquentLoansOverThirtyDaysCount ? ((dealer.dealerMetric.delinquentLoansOverThirtyDaysCount / dealer.dealerMetric.totalFundedLoans) * 100).toFixed(2) : '0.00',
            dealer.loanTypes,
            dealer.createdAt,
            dealer.testTransStatus,
            dealer.hasFincenId
        ];
});

const changePage = (setTableConfig, page) => {
    setTableConfig(state => ({ ...state, page: parseInt(page), }));
}

const changeSort = (setTableConfig, sortOrder) => {
    setTableConfig(state => ({
        ...state,
        page: 0,  // Sorting resets pagination
        sortOrder: sortOrder,
    }));
}

const changeSearch = (setTableConfig, searchText) => {
    // Note: Clear out the page and sort state first
    setTableConfig(state => ({
        ...state,
        searchText: searchText?.trim() ?? '',
        page: 0,  // Searching resets pagination
        sortOrder: {},
    }));
}

const changeFilter = (setTableConfig, applyFilters) => {
    let filterList = applyFilters();

    // Note: Clear out the page and sort state first
    setTableConfig(state => ({
        ...state,
        filterList: filterList,
        page: 0,  // Filtering resets pagination
        sortOrder: {},
    }));
}

const DealersTable = ({ toggleShowUpdateDealer, setCurrentDealer }) => {
    const { showError } = useError();
    const starterColumns = ['Dealer Number', 'Name', 'Organization', 'Account Flag', 'Comments', 'Manual Flag', 'Loan Count', 'Max Loan Amount', 'Total Amount Loaned', 'Average Loan Amount', 'Notification Emails', 'Concierge Dealer', 'Config'];
    let visibleColumns = sessionStorage.getItem("dealerTable") ? getTableSession("dealerTable") : saveTableSession(starterColumns, "dealerTable");

    const [tableConfig, setTableConfig] = useState({
        page: 0,
        count: 0,
        rowsPerPage: ROWS_PER_PAGE,
        sortOrder: {},
        searchText: '',
        visibleColumns: visibleColumns,
        data: [],
        isLoading: true,
    });

    useEffect(() => {
        setTableConfig(state => ({ ...state, isLoading: true, }));

        DealerService.getAllPaginated(tableConfig.page + 1, tableConfig.rowsPerPage, tableConfig.searchText, tableConfig.filterList).then(res => {
            if (res) {
                setTableConfig(state => ({
                    ...state,
                    isLoading: false,
                    count: res.meta.count,
                    data: res.dealers,
                }));
            } else {
                showError("Failed to retrieve dealers, try refreshing...");
            }
        }).catch(err => {
            showError(err);
            setTableConfig(state => ({ ...state, isLoading: false, }));
        });
    }, [tableConfig.page, tableConfig.searchText, tableConfig.filterList]);

    const { isDealerBrowser } = useRole();
    const options = getTableOptions(setTableConfig, tableConfig.count, tableConfig.page, tableConfig.sortOrder, tableConfig.searchText);
    const dealerData = tablifyDealers(tableConfig.data, tableConfig.isLoading);

    return dealerData == null ? (
        <InlineLoadingText/>
    ) : (
        <MUIDataTable data={dealerData} columns={getColumns(toggleShowUpdateDealer, setCurrentDealer, isDealerBrowser, tableConfig.visibleColumns)} options={options} />
    );
}

export default memo(DealersTable);
